import { mapState } from 'vuex'
import { util } from '@didi/soda-common'
import { membershipLandingCk } from './trackEvent.js'
import { MEMBER_STATUS, PAGE_FROM, CYCLE_TYPE } from './common/enumsUtil.js'
import { getPageFromParam } from '@/page/member/common/utils.js'
import { isEmpty } from 'lodash-es'

const { env } = util

export default {
  computed: {
    ...mapState(['membership', 'foodNotOpen', 'transNationalInfo']),
    /** ------------------------ 页面来源逻辑 ------------------------ */
    pageFrom() {
      const pageFromParam = getPageFromParam(this.params)
      if (['RideSidebar', 'SAAccount', 'rideside'].includes(pageFromParam)) {
        return PAGE_FROM.fromEmbed
      } else if (pageFromParam === 'bill') {
        return PAGE_FROM.fromBill
      } else if (['tab', 'C_Bottom_Nav_A_1_0', 'C_Bottom_Nav_B_1_0'].includes(pageFromParam)) {
        return PAGE_FROM.fromTab
      } else if (pageFromParam === 'Rpage') {
        return PAGE_FROM.fromShop
      } else if (pageFromParam === 'RideSidebar') {
        return PAGE_FROM.fromEmbedSidebar
      }
    },
    // 是不是Android
    isAndroid() {
      return env.isAndroid
    },
    // 嵌入端
    isFromEmbed() {
      return this.pageFrom === PAGE_FROM.fromEmbed
    },
    // 提单页
    isFromBill() {
      return this.pageFrom === PAGE_FROM.fromBill
    },
    // 底导
    isFromTab() {
      return this.pageFrom === PAGE_FROM.fromTab
    },
    // 商家页
    isFromShop() {
      return this.pageFrom === PAGE_FROM.fromShop
    },
    // 出行侧边栏进入
    isFromEmbedSidebar() {
      return this.pageFrom === PAGE_FROM.fromEmbedSidebar
    },
    /** ------------------------ 用户状态 ------------------------ */
    memberStatus() {
      let status = ''
      if (this.membership?.isNewUser) {
        // 会员新客(从未获得过会员的用户)
        status = MEMBER_STATUS.newNoBuy
      } else if (this.membership?.status === 1 && this.membership?.subscriptionStatus === 1) {
        // 会员老客-未开通&未订阅
        status = MEMBER_STATUS.oldNoBuyNoSubscription
      } else if (this.membership?.status === 1 && this.membership?.subscriptionStatus === 2) {
        // 会员老客-未开通&已订阅
        status = MEMBER_STATUS.oldNoBuySubscription
      } else if (this.membership?.status === 1 && this.membership?.subscriptionStatus === 3) {
        // 会员老客-未开通&暂停订阅
        status = MEMBER_STATUS.oldNoBuyPaused
      } else if (this.membership?.status === 2 && this.membership?.subscriptionStatus === 2) {
        // 会员老客-已开通&已订阅
        status = MEMBER_STATUS.oldBuySubscription
      } else if (this.membership?.status === 2 && this.membership?.subscriptionStatus === 1) {
        // 会员老客-已开通&未订阅
        status = MEMBER_STATUS.oldBuyNoSubscription
      } else if (this.membership?.status === 2 && this.membership?.subscriptionStatus === 3) {
        // 会员老客-已开通&暂停订阅
        status = MEMBER_STATUS.oldBuyPaused
      }
      return status
    },
    isNewUserStatus() {
      return this.memberStatus === MEMBER_STATUS.newNoBuy
    },
    isPauseStatus() {
      // 会员老客-未开通&暂停订阅 || 会员老客-已开通&暂停订阅
      return (
        this.memberStatus === MEMBER_STATUS.oldNoBuyPaused ||
        this.memberStatus === MEMBER_STATUS.oldBuyPaused
      )
    },
    isBuySubscriptionStatus() {
      return this.memberStatus === MEMBER_STATUS.oldBuySubscription
    },
    isNoBuySubscriptionStatus() {
      return this.memberStatus === MEMBER_STATUS.oldNoBuySubscription
    },
    isSubscriptionPauseStatus() {
      return (
        this.memberStatus === MEMBER_STATUS.oldNoBuyPaused ||
        this.memberStatus === MEMBER_STATUS.oldBuySubscription
      )
    },
    isChangePaymentMixin() {
      return (
        this.memberStatus === MEMBER_STATUS.oldNoBuyPaused ||
        this.memberStatus === MEMBER_STATUS.oldBuySubscription ||
        this.memberStatus === MEMBER_STATUS.oldBuyPaused
      )
    },
    isNoBuyStatus() {
      return [
        MEMBER_STATUS.newNoBuy,
        MEMBER_STATUS.oldNoBuyNoSubscription,
        MEMBER_STATUS.oldNoBuySubscription,
        MEMBER_STATUS.oldNoBuyPaused,
      ].includes(this.memberStatus)
    },
    // 会员老客-已开通&暂停订阅
    isBuyPauseStatus() {
      return this.memberStatus === MEMBER_STATUS.oldBuyPaused
    },
    // 会员老客-未开通&暂停订阅
    isNoBuyPausedStatus() {
      return this.memberStatus === MEMBER_STATUS.oldNoBuyPaused
    },
    isBuyNoSubscriptionStatus() {
      return this.memberStatus === MEMBER_STATUS.oldBuyNoSubscription
    },
    // pack->升级clue,特殊状态
    isUpgradesProStatus() {
      return this.membership?.status === 3
    },
    isBuyStatus() {
      return [
        MEMBER_STATUS.oldBuySubscription,
        MEMBER_STATUS.oldBuyNoSubscription,
        MEMBER_STATUS.oldBuyPaused,
      ].includes(this.memberStatus)
    },
    // 是否为免费送开通的会员
    isFreeGiveStatus() {
      return +this.membership?.openStatus === 1
    },
    /** ------------------------ 会员长周期扩展状态 ------------------------ */
    // 是否可购买长周期会员
    canPurchaseLongTerm() {
      return this.membership?.canPurchaseLongTerm
    },
    // 可购买长周期会员
    purchasableLongTerm() {
      const purchasableOptionList = []
      this.membership?.purchasableOptionList?.forEach(item => {
        if (item.cycleType !== CYCLE_TYPE.month) {
          purchasableOptionList.push(item)
        }
      })
      return purchasableOptionList
    },
    // 已购买长周期会员
    willLongTerm() {
      const willEnableList = []
      this.membership?.willEnableList?.forEach(item => {
        // type 将要生效会员类型: 0-默认 1-长周期升级 2-免费送
        if (item.cycleType !== CYCLE_TYPE.month && +item.type === 1) {
          willEnableList.push(item)
        }
      })
      return willEnableList
    },
    // 当前生效月会员
    isMonthlyMembership() {
      return this.membership?.cycleType === CYCLE_TYPE.month
    },

    // 升级入口：已开通 & 可购买长期会员
    isShowUpgradeLongTerm() {
      const tag = this.isBuyStatus && this.canPurchaseLongTerm
      return tag
      // 已开通 & 当前生效月会员 & 可购买长期会员
      // return (
      //   this.isBuyStatus &&
      //   this.canPurchaseLongTerm &&
      //   isEmpty(this.willLongTerm) &&
      //   this.isMonthlyMembership
      // )
    },
    // 升级开通：首个长周期会员
    upgradeLongTerm() {
      return this.purchasableLongTerm[0]
    },
    // 权益信息条展示：已开通 & 已订阅 & 待生效长周期会员 & 但仍处于前一个月度会员有效期内
    isShowLongTerm() {
      return this.isBuySubscriptionStatus && !isEmpty(this.willLongTerm) && this.isMonthlyMembership
    },
    // 暂停按钮展示：已开通 & 已订阅 & 当前生效非长周期会员 & 非待生效长周期会员 && 非免费赠送n个月会员
    isShowPause() {
      return (
        this.isBuySubscriptionStatus &&
        this.isMonthlyMembership &&
        isEmpty(this.willLongTerm) &&
        !this.isFreeGiveStatus
      )
    },
    // 底部订阅文案展示：已开通&已订阅 && 不是（ 待生效长周期会员 且 仍处于前一个月度会员有效期内）
    isShowSubscribeText() {
      return (
        this.isBuySubscriptionStatus && !(!isEmpty(this.willLongTerm) && this.isMonthlyMembership)
      )
    },
    /** ------------------------ 埋点用户状态 ------------------------ */
    // 会员用户状态 0：未开通 - 已/未订阅； 1：已开通-且订阅；2:已开通-未订阅 3:会员锁定状态 4:未开通-订阅暂停 5: 已开通-订阅暂停 6: 新用户
    couponPackageStatus() {
      // lockStatus=2 的时候为锁定状态
      if (+this.membership?.lockStatus === 2) {
        return 3
      }
      if (this.membership?.status === 1) {
        if (this.membership?.isNewUser) {
          return 6
        } else if (this.membership?.subscriptionStatus === 3) {
          return 4
        } else {
          return 0
        }
      } else {
        // 订阅状态：1-未订阅  2-已订阅 3-订阅暂停
        if (this.membership?.subscriptionStatus === 3) {
          return 5
        } else if (this.membership?.subscriptionStatus === 2) {
          if (this.isFreeGiveStatus) {
            return 10 // 已开通（赠送n个月）- 已订阅
          }
          if (!this.isMonthlyMembership) {
            return 7
          } else {
            return 1
          }
        } else {
          if (this.isFreeGiveStatus) {
            return 9 // 已开通（赠送n个月）- 未订阅
          }
          if (!this.isMonthlyMembership) {
            return 8
          } else {
            return 2
          }
        }
      }
    },

    /** ------------------------ 通用状态 ------------------------ */
    brandName() {
      return this.membership?.brandName
    },
    /** ------------------------ 实验开关 ------------------------ */
    isGlobaloneStyle() {
      return this.membership?.testConfig?.memberGlobalOne > 1 // 大于1 命中实验组采用新样式
    },
    // AB实验：命中首屏内容精简优化实验 25.02  （1对照组、2实验组）
    isEasyInfoTest() {
      return +this.membership?.testConfig?.memberBenefitSubtitle === 2
    },
    // AB实验：命中会员订单详情页  （1对照组、2实验组）
    isMemberOrderDetail() {
      return +this.membership?.testConfig?.memberOrderDetail === 2
    },
    /** ------------------------ 澳洲开国 ------------------------ */
    foodOpen() {
      return this.foodNotOpen === 0 // 1-未在外卖开国 0-已在外卖开国
    },
    // 跨国订阅列表
    transNationalMemberList() {
      return this.transNationalInfo?.membershipList || []
    },
    // 是否有跨国订阅信息
    hasTransNationalInfo() {
      return this.transNationalMemberList?.length > 0
    },
  },
  methods: {
    /** ------------------------ 点击埋点 ------------------------ */
    membershipLandingCkFn(click_type) {
      membershipLandingCk({
        coupon_package_status: this.couponPackageStatus,
        benefit_id: this.membership?.benefitID,
        click_type,
        benefit_type: +this.membership?.status,
        add_coupon_list: null,
        from: getPageFromParam(this.params),
        sell_price: this.membership?.price,
        original_price: this.membership?.displayPrice || -1,
      })
    },
  },
}
